// src/components/Admin/Category/CategorySearch.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAllCategoryBasicData } from '../../../hooks/UseCategoryData';
import { useNavigate, useParams } from 'react-router-dom'; 
import { useAdminStore } from '../../../store/adminStore'; 

export function CategorySearch({ locale, onSelect, typeFilter = null, placeholder = "Search category...", initialQuery = '' }) {
  const { categories, loading } = useAllCategoryBasicData(locale);
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  
  const ulRef = useRef();
  const navigate = useNavigate();
  const params = useParams();
  const storeLocale = useAdminStore((state) => state.locale);
  const currentLocale = params.locale || storeLocale; 

  useEffect(() => {
    if (initialQuery) setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    const lowerQuery = query.toLowerCase();

    // 1. First, filter the master list by type if provided (e.g. 'parent' or 'child')
    let sourceData = categories;
    if (typeFilter) {
        sourceData = categories.filter(c => c.type === typeFilter);
    }

    // 2. Perform Search on filtered data
    const matchedChildren = sourceData.filter(
      cat => cat.type === 'child' && cat.name.toLowerCase().includes(lowerQuery)
    );

    // Handle avatars (aliases)
    const matchedAvatars = sourceData.filter(
      cat =>
        cat.type === 'avatar' &&
        matchedChildren.some(child => child.id === cat.inherits_from)
    );

    const directMatches = sourceData.filter(
      cat => cat.name.toLowerCase().includes(lowerQuery)
    );

    const combined = Array.from(
      new Map([...directMatches, ...matchedAvatars].map(cat => [cat.id, cat])).values()
    );

    setSuggestions(combined);
    setHighlightIndex(-1);
  }, [query, categories, typeFilter]);

  const handleSelect = (cat) => {
    setQuery(cat.name); // Update input to show selected name
    setSuggestions([]);
    if (onSelect) {
      onSelect(cat);    
    } else {
      if (!currentLocale) {
          navigate(`/admin/categories/manage`);
          return;
      }
      navigate(`/admin/categories/manage/${currentLocale}/view/${cat.id}/info`);
    }
  };

  return (
    <div className="relative w-full mb-2">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      {suggestions.length > 0 && (
        <ul
          ref={ulRef}
          className="absolute bg-white border border-gray-200 w-full rounded shadow-lg z-50 mt-1 max-h-48 overflow-y-auto"
        >
          {suggestions.map((cat, index) => (
            <li
              key={cat.id}
              onClick={() => handleSelect(cat)}
              className={`px-4 py-2 cursor-pointer flex justify-between items-center text-sm ${
                index === highlightIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <span className="font-medium">{cat.name}</span>
              <span className="text-[10px] text-gray-400 uppercase">
                {cat.type}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}